import { getMeCapabilities } from '@vimeo/gctl-api/fetchers/me/capabilities';
import { getUserCapabilities } from '@vimeo/gctl-api/fetchers/user/capabilities';

import type { Capability, CapabilityNameMap, UserCapabilities } from './capabilityTypes';
import { getKeys } from './utils';

/**
 * Retrieve the user capabilities from the API. Also handles stripping the `can`
 * and `has` prefixes for the request and then adding them back into the response.
 */
export async function fetchAndFormatCapabilties({
  apiUrl,
  capabilities,
  headers,
  jwt,
  userId,
}: {
  apiUrl: string;
  capabilities: Capability[];
  headers?: Record<string, string | string[]>;
  jwt: string;
  userId?: string | number;
}) {
  // We're in a weird place where the API expects the field name to _not_ include
  // the `can` and `has` prefixes, but we call `useCapability` with values that
  // _do_ have the `can` and `has` prefixes. So we do some dancing to strip the prefixes
  // in the API request and then slap them back on to the response. :facepalm:
  const capabilityMap: CapabilityNameMap = capabilities.reduce((acc, c) => {
    const field = c.match(/^(can|has)(.+)/)?.[2];
    const key = field ? field[0].toLowerCase() + field.slice(1) : c;
    acc[key] = c;
    return acc;
  }, {});

  const requestFields = {
    select: getKeys(capabilityMap),
    headers: {
      Authorization: `jwt ${jwt}`,
      'Content-Type': 'application/json',
      ...(headers || {}),
    },
    baseUrl: apiUrl.startsWith('https://') ? apiUrl : `https://${apiUrl}`,
  };

  let response: UserCapabilities;
  if (userId) {
    response = await getUserCapabilities({
      ...requestFields,
      where: { userId: Number(userId) },
    });
  } else {
    response = await getMeCapabilities(requestFields);
  }

  // Add the `can` and `has` prefixes back onto the response fields.
  const formattedCaps = Object.keys(response).reduce((acc, key) => {
    const flag = response[key];
    if (typeof flag === 'boolean' && capabilityMap[key]) {
      acc[capabilityMap[key]] = flag;
    } else {
      throw new Error('response is not well formed');
    }
    return acc;
  }, {} as UserCapabilities);

  return formattedCaps;
}
